/**
 * Partner connect page (EPD-11850) — reached via `/connect?source=<partner>&return=<url>`.
 *
 * Deliberately outside the onboarding gate (that lives on the org index
 * route), so partner-sourced creators go straight to connecting accounts.
 * Rendered without app chrome (see OrgLayout).
 */

import {
  type PartnerConnectNavigation,
  PartnerConnectFlow,
} from "@/components/partner-connect/partner-connect-flow";
import {
  getPartnerSource,
  MAX_RETURN_URL_LENGTH,
  resolvePartnerReturnUrl,
} from "@/lib/partner-source";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

const platformSchema = z.enum(["instagram", "tiktok", "youtube"]);

export const partnerConnectSearchSchema = z.object({
  source: z.string().optional().catch(undefined),
  // The partner's return URL from the original link, carried through every step
  return: z.string().max(MAX_RETURN_URL_LENGTH).optional().catch(undefined),
  step: z
    .enum(["list", "connect", "done", "error"])
    .optional()
    .catch(undefined),
  platform: platformSchema.optional().catch(undefined),
  // User-facing OAuth error forwarded by the callback page
  message: z.string().max(500).optional().catch(undefined),
});

export const Route = createFileRoute("/(org)/o/$orgSlug/connect")({
  validateSearch: partnerConnectSearchSchema,
  beforeLoad: ({ search, params }) => {
    // Without a known source this isn't a partner flow — send to the org home,
    // which applies the standard onboarding gate.
    if (!getPartnerSource(search.source)) {
      throw redirect({ to: "/o/$orgSlug", params });
    }
  },
  component: PartnerConnectPage,
});

function PartnerConnectPage() {
  const { organization, role } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const partner = getPartnerSource(search.source);
  if (!partner) return null;

  // A platform outside this partner's list falls back to the platform list
  const platform =
    search.platform && partner.platforms.includes(search.platform)
      ? search.platform
      : undefined;

  const handleNavigate = (nav: PartnerConnectNavigation) => {
    // Replace, not push: Back from the success screen must not land on the
    // intermediary step and restart OAuth. Dropping `message` is intended.
    navigate({
      replace: true,
      search: {
        source: partner.id,
        return: search.return,
        step: nav.step,
        platform: platformSchema.safeParse(nav.platform).data,
      },
    });
  };

  return (
    <PartnerConnectFlow
      partner={partner}
      returnUrl={resolvePartnerReturnUrl(partner, search.return)}
      requestedReturn={search.return}
      organization={organization}
      isOwner={role === "owner"}
      step={search.step ?? "list"}
      platform={platform}
      message={search.message}
      onNavigate={handleNavigate}
    />
  );
}
